// Product Detail Page - Shows individual product with buy button
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useCart } from '../contexts/CartContext';
import ProductCard from '../components/ProductCard';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    loadProduct();
  }, [id]);

  // Calculate stock before using in effects
  const totalQuantity = inventory.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const inStock = totalQuantity > 0;

  // Update SEO meta tags and structured data when product loads
  useEffect(() => {
    if (product) {
      // Update page title
      document.title = `${product.description || product.name} - Magnolia Mold Co`;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 
          `${product.description || product.name} - $${product.price.toFixed(2)}. Handcrafted polymer clay earrings. ${inStock ? 'In stock' : 'Out of stock'}.`
        );
      }

      // Update Open Graph tags
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', `${product.description || product.name} - Magnolia Mold Co`);
      }

      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogDescription) {
        ogDescription.setAttribute('content', 
          `${product.description || product.name} - $${product.price.toFixed(2)}. Handcrafted polymer clay earrings.`
        );
      }

      let ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage && product.photoUrl) {
        ogImage.setAttribute('content', product.photoUrl);
      }

      let ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) {
        ogUrl.setAttribute('content', `https://magnoliamoldco.com/product/${id}`);
      }

      // Add canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', `https://magnoliamoldco.com/product/${id}`);

      // Add structured data (JSON-LD) for product
      let structuredData = document.querySelector('#product-structured-data');
      if (!structuredData) {
        structuredData = document.createElement('script');
        structuredData.setAttribute('type', 'application/ld+json');
        structuredData.setAttribute('id', 'product-structured-data');
        document.head.appendChild(structuredData);
      }

      const schema = {
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": product.description || product.name,
        "image": product.photoUrl || "https://magnoliamoldco.com/logo.png",
        "description": `${product.description || product.name}. Handcrafted polymer clay earrings made with love and attention to detail.`,
        "sku": product.sku,
        "brand": {
          "@type": "Brand",
          "name": "Magnolia Mold Co"
        },
        "offers": {
          "@type": "Offer",
          "url": `https://magnoliamoldco.com/product/${id}`,
          "priceCurrency": "USD",
          "price": product.price.toFixed(2),
          "availability": inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "seller": {
            "@type": "Organization",
            "name": "Magnolia Mold Co"
          }
        }
      };

      structuredData.textContent = JSON.stringify(schema);
    }

    // Cleanup function to reset title when leaving page
    return () => {
      document.title = 'Magnolia Mold Co - Handcrafted Polymer Clay Earrings';
      
      // Remove product structured data when leaving page
      const structuredData = document.querySelector('#product-structured-data');
      if (structuredData) {
        structuredData.remove();
      }

      // Remove canonical if it was added
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical && canonical.getAttribute('href').includes('/product/')) {
        canonical.setAttribute('href', 'https://magnoliamoldco.com/');
      }
    };
  }, [product, id, inStock]);

  const loadProduct = async () => {
    try {
      // Use public API (no authentication needed)
      const response = await fetch(`${API_URL}/api/public/products/${id}`);
      
      if (!response.ok) {
        navigate('/shop');
        return;
      }

      const data = await response.json();
      setProduct(data.product);
      setInventory(data.inventory || []);
      
      // Load related products (same type)
      if (data.product?.name) {
        const relatedResponse = await fetch(`${API_URL}/api/public/products?type=${encodeURIComponent(data.product.name)}`);
        if (relatedResponse.ok) {
          const relatedData = await relatedResponse.json();
          // Filter out current product and limit to 4
          const related = relatedData.products
            .filter(p => p.id !== id)
            .slice(0, 4);
          setRelatedProducts(related);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading product:', error);
      setLoading(false);
      navigate('/shop');
    }
  };

  const handleAddToCart = () => {
    addToCart(product, selectedQuantity);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-cream-300 border-t-sage-600" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-3xl text-ink mb-4">Product not found</h2>
          <Link to="/shop" className="text-sage-700 hover:text-sage-800 underline underline-offset-4">
            Back to the shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm text-ink-muted flex items-center gap-2">
          <Link to="/shop" className="hover:text-sage-700 transition-colors">Shop</Link>
          <span aria-hidden="true">/</span>
          <span>{product.name}</span>
        </nav>

        {/* Product Detail */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Product Image */}
          <div className="aspect-square bg-cream-200 rounded-2xl overflow-hidden">
            {product.photoUrl ? (
              <img
                src={product.photoUrl}
                alt={product.description || product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <img src="/logo.png" alt="" className="w-1/2 opacity-40" />
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex flex-col lg:py-4">
            <p className="eyebrow mb-3">{product.name}</p>
            <h1 className="font-serif text-4xl md:text-5xl font-semibold text-ink capitalize leading-tight mb-4">
              {product.description || product.name}
            </h1>
            <p className="text-2xl text-sage-700 font-medium mb-6">
              ${product.price.toFixed(2)}
            </p>

            {/* Stock Status */}
            <div className="mb-8 text-sm">
              {inStock ? (
                <span className="inline-flex items-center gap-2 text-sage-700">
                  <span className="w-2 h-2 rounded-full bg-sage-500" aria-hidden="true" />
                  In stock · {totalQuantity} available
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 text-ink-muted">
                  <span className="w-2 h-2 rounded-full bg-ink-muted" aria-hidden="true" />
                  Sold out
                </span>
              )}
            </div>

            {/* Quantity Selector & Add to Cart */}
            {inStock && (
              <div className="flex items-stretch gap-3 mb-10">
                <select
                  value={selectedQuantity}
                  onChange={(e) => setSelectedQuantity(parseInt(e.target.value))}
                  className="field w-24"
                  aria-label="Quantity"
                >
                  {[...Array(Math.min(10, totalQuantity))].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
                <button onClick={handleAddToCart} className="btn-brand flex-1 py-3.5">
                  {(product.checkoutUrl || product.squarespaceUrl) ? 'Buy Now' : 'Add to Cart'}
                </button>
              </div>
            )}

            {/* Product Description */}
            <div className="border-t border-cream-300 pt-6">
              <h2 className="font-serif text-2xl font-semibold text-ink mb-3">Details</h2>
              <dl className="text-sm text-ink-soft space-y-2">
                <div className="flex gap-2"><dt className="text-ink-muted w-16">Type</dt><dd>{product.name}</dd></div>
                <div className="flex gap-2"><dt className="text-ink-muted w-16">SKU</dt><dd>{product.sku}</dd></div>
              </dl>
              <p className="text-ink-soft mt-5 leading-relaxed">
                Each pair is lovingly handcrafted from polymer clay with attention to detail and quality,
                so no two are exactly alike.
              </p>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-24">
            <div className="text-center mb-10">
              <h2 className="section-title text-3xl md:text-4xl mb-4">You May Also Like</h2>
              <div className="flourish" aria-hidden="true">✦</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
